'use client';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cx, mergeRefs, getSelectButtonColors, hasValue } from '@UI/lib/utils';
import { Icon as IconButton } from '@UI/components/Icon';
import {
  RiErrorWarningLine,
  RiEyeCloseLine,
  RiEyeLine,
} from '@remixicon/react';

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: 'text' | 'password' | 'email' | 'url' | 'number' | 'search' | 'tel';
  defaultValue?: string | number;
  value?: string | number;
  icon?: React.ElementType;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  stepper?: ReactNode;
  prefix?: string;
  onValueChange?: (value: string | number) => void;
  pattern?: string;
}

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (props, ref) => {
    const {
      value,
      defaultValue,
      type,
      placeholder = 'Type...',
      icon,
      error = false,
      errorMessage,
      disabled = false,
      stepper,
      prefix,
      className,
      onChange,
      onValueChange,
      autoFocus,
      pattern,
      ...other
    } = props;

    const [isFocused, setIsFocused] = useState<boolean>(autoFocus || false);
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

    const toggleIsPasswordVisible = useCallback(
      () => setIsPasswordVisible(!isPasswordVisible),
      [isPasswordVisible, setIsPasswordVisible],
    );

    const Icon = icon;

    const inputRef = useRef<HTMLInputElement>(null);

    const hasSelection = hasValue(value || defaultValue);

    useEffect(() => {
      const handleFocus = () => setIsFocused(true);
      const handleBlur = () => setIsFocused(false);

      const node = inputRef.current;

      if (node) {
        node.addEventListener('focus', handleFocus);
        node.addEventListener('blur', handleBlur);
        // Autofocus logic
        if (autoFocus) {
          node.focus();
        }
      }

      return () => {
        if (node) {
          node.removeEventListener('focus', handleFocus);
          node.removeEventListener('blur', handleBlur);
        }
      };
    }, [autoFocus]);

    return (
      <>
        <div
          className={cx(
            // common
            'relative w-full flex items-center min-w-[10rem] outline-none rounded-revving-default transition duration-100 border',
            // light
            'shadow-revving-input',
            // dark
            'dark:shadow-darkRevving-input',
            getSelectButtonColors(hasSelection, disabled, error),
            isFocused &&
              cx(
                // common
                'ring-2',
                // light
                'border-revving-brand-subtle ring-revving-brand-muted',
                // light
                'dark:border-darkRevving-brand-subtle dark:ring-darkRevving-brand-muted',
              ),
            className,
          )}
        >
          {Icon ? (
            <Icon
              className={cx(
                // common
                'shrink-0 h-5 w-5 mx-2.5 absolute left-0 flex items-center',
                // light
                'text-revving-content-subtle',
                // light
                'dark:text-darkRevving-content-subtle',
              )}
            />
          ) : null}

          {prefix ? (
            <div className={cx(Icon ? 'pl-10' : 'pl-3')}>{prefix}</div>
          ) : null}

          <input
            ref={mergeRefs([inputRef, ref])}
            defaultValue={defaultValue}
            value={value}
            type={isPasswordVisible ? 'text' : type}
            className={cx(
              // common
              'w-full bg-transparent focus:outline-none focus:ring-0 border-none text-revving-default rounded-revving-default transition duration-100 py-2',
              // light
              'text-revving-content-emphasis',
              // dark
              'dark:text-darkRevving-content-emphasis',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              type === 'password'
                ? error
                  ? 'pr-16'
                  : 'pr-12'
                : error
                  ? 'pr-8'
                  : 'pr-3',
              prefix ? 'ml-2' : Icon ? 'pl-10' : 'pl-3',
              disabled
                ? 'placeholder:text-revving-content-subtle dark:placeholder:text-darkRevving-content-subtle'
                : 'placeholder:text-revving-content dark:placeholder:text-darkRevving-content',
            )}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              onChange?.(e);
              onValueChange?.(e.target.value);
            }}
            pattern={pattern}
            {...other}
          />

          {type === 'password' && !disabled ? (
            <button
              className="absolute inset-y-0 right-0 flex items-center px-2.5 rounded-lg"
              type="button"
              onClick={() => toggleIsPasswordVisible()}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show Password'}
            >
              {isPasswordVisible ? (
                <IconButton
                  icon={RiEyeCloseLine}
                  className={cx(
                    // common
                    'flex-none h-5 w-5 transition',
                    // light
                    'text-revving-content-subtle hover:text-revving-content',
                    // dark
                    'dark:text-darkRevving-content-subtle hover:dark:text-darkRevving-content',
                  )}
                  aria-hidden
                />
              ) : (
                <IconButton
                  icon={RiEyeLine}
                  className={cx(
                    // common
                    'flex-none h-5 w-5 transition',
                    // light
                    'text-revving-content-subtle hover:text-revving-content',
                    // dark
                    'dark:text-darkRevving-content-subtle hover:dark:text-darkRevving-content',
                  )}
                  aria-hidden
                />
              )}
            </button>
          ) : null}

          {error ? (
            <IconButton
              icon={RiErrorWarningLine}
              className={cx(
                'text-red-500 shrink-0 h-5 w-5 absolute right-0 flex items-center',
                type === 'password'
                  ? 'mr-10'
                  : type === 'number'
                    ? stepper
                      ? 'mr-20'
                      : 'mr-3'
                    : 'mx-2.5',
              )}
            />
          ) : null}
          {stepper ?? null}
        </div>

        {error && errorMessage ? (
          <p className="text-sm text-red-500 mt-1">{errorMessage}</p>
        ) : null}
      </>
    );
  },
);

BaseInput.displayName = 'BaseInput';

export { BaseInput, type BaseInputProps };
